import React, { useState, useRef, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { 
  FaHome, 
  FaStore, 
  FaClipboardList, 
  FaUsers, 
  FaChalkboardTeacher, 
  FaCog, 
  FaSignOutAlt, 
  FaBars, 
  FaTimes, 
  FaBell,
  FaUser,
  FaUserCog,
  FaShoppingBag,
  FaChartLine,
  FaListAlt,
  FaCreditCard,
} from 'react-icons/fa';
import { signOut } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import logoImg from '../assets/logo-godaiStudios.png';

const Container = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: #f5f5f5;
`;

const Sidebar = styled.div`
  width: ${props => props.collapsed ? '80px' : '270px'};
  background-color: #222;
  color: white;
  transition: width 0.3s;
  position: fixed;
  height: 100vh;
  overflow-y: auto;
  z-index: 1000;
  
  @media (max-width: 768px) {
    width: ${props => props.open ? '270px' : '0'};
    left: ${props => props.open ? '0' : '-270px'};
  }
`;

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${props => props.collapsed ? 'center' : 'space-between'};
  padding: ${props => props.collapsed ? '25px 0' : '25px'};
  border-bottom: 1px solid #333;
`;

const Logo = styled.div`
  font-size: ${props => props.collapsed ? '18px' : '22px'};
  font-weight: 600;
  color: white;
  display: flex;
  align-items: center;
  
  img {
    height: 40px;
    width: auto;
    object-fit: contain;
    margin-right: ${props => props.collapsed ? '0' : '10px'};
  }
  
  span {
    display: ${props => props.collapsed ? 'none' : 'block'};
  }
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: #aaa;
  font-size: 18px;
  cursor: pointer;
  display: ${props => props.collapsed ? 'none' : 'block'};
  
  &:hover {
    color: white;
  }
`;

const NavItems = styled.div`
  padding: 20px 0;
`;

const NavItem = styled(Link)`
  display: flex;
  align-items: center;
  padding: ${props => props.collapsed ? '16px 0' : '16px 25px'};
  justify-content: ${props => props.collapsed ? 'center' : 'flex-start'};
  color: ${props => props.active ? 'white' : '#aaa'};
  text-decoration: none;
  transition: all 0.2s;
  border-left: 4px solid ${props => props.active ? '#4caf50' : 'transparent'};
  background-color: ${props => props.active ? '#333' : 'transparent'};
  
  &:hover {
    color: white;
    background-color: #333;
  }
  
  .icon {
    font-size: 18px;
    min-width: 30px;
    text-align: center;
  }
  
  .text {
    display: ${props => props.collapsed ? 'none' : 'block'};
    margin-left: ${props => props.collapsed ? '0' : '10px'};
    font-size: 15px;
  }
`;

const NavCategory = styled.div`
  color: #777;
  font-size: 12px;
  text-transform: uppercase;
  padding: ${props => props.collapsed ? '20px 0 10px' : '20px 25px 10px'};
  display: ${props => props.collapsed ? 'none' : 'block'};
  letter-spacing: 1px;
`;

const Content = styled.div`
  flex: 1;
  margin-left: ${props => props.collapsed ? '80px' : '270px'};
  width: ${props => props.collapsed ? 'calc(100% - 80px)' : 'calc(100% - 270px)'};
  transition: all 0.3s;
  
  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
  }
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 30px;
  background-color: white;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  position: sticky;
  top: 0;
  z-index: 900;
`;

const MobileMenuButton = styled.button`
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
  color: #333;
  display: none;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const NotificationBell = styled.div`
  position: relative;
  font-size: 18px;
  color: #555;
  cursor: pointer;
  
  .badge {
    position: absolute;
    top: -5px;
    right: -5px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background-color: #f44336;
    color: white;
    font-size: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const UserProfileContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;
  
  .avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background-color: #ddd;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #666;
    margin-right: 10px;
  }
  
  .user-info {
    display: flex;
    flex-direction: column;
    
    .name {
      font-weight: 500;
      font-size: 14px;
    }
    
    .role {
      font-size: 12px;
      color: #777;
    }
  }
  
  @media (max-width: 576px) {
    .user-info {
      display: none;
    }
  }
`;

const UserDropdown = styled.div`
  position: absolute;
  top: 45px;
  right: 0;
  background-color: white;
  border-radius: 5px;
  min-width: 180px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  display: ${props => props.isOpen ? 'block' : 'none'};
  z-index: 1001;
  overflow: hidden;
`;

const DropdownItem = styled(Link)`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  color: #333;
  text-decoration: none;
  font-size: 14px;
  transition: background-color 0.2s;
  
  svg {
    margin-right: 10px;
    font-size: 16px;
  }
  
  &:hover {
    background-color: #f5f5f5;
  }
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  width: 100%;
  background: none;
  border: none;
  color: #333;
  text-align: left;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  svg {
    margin-right: 10px;
    font-size: 16px;
  }
  
  &:hover {
    background-color: #f5f5f5;
  }
`;

// Create a forwarded ref component
const UserProfile = React.forwardRef((props, ref) => (
  <UserProfileContainer ref={ref} {...props} />
));

const PageContent = styled.main`
  padding: 30px;
  height: 100%;
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 900;
  display: ${props => props.show ? 'block' : 'none'};
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const Layout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const userMenuRef = useRef(null);
  
  const user = auth.currentUser;
  
  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };
  
  const toggleSidebar = () => {
    setCollapsed(!collapsed);
  };
  
  const toggleMobileSidebar = () => {
    setMobileOpen(!mobileOpen);
  };
  
  const closeMobileSidebar = () => {
    if (mobileOpen) {
      setMobileOpen(false);
    }
  };
  
  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };
  
  // Close user dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    // Add event listener when dropdown is open
    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Cleanup the event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);
  
  const isActive = (path) => {
    return location.pathname === path;
  };
  
  return (
    <Container>
      <Sidebar collapsed={collapsed} open={mobileOpen}>
        <SidebarHeader collapsed={collapsed}>
          <Logo collapsed={collapsed}>
            <img src={logoImg} alt="Godai Studios Logo" />
            <span>Admin Portal</span>
          </Logo>
          <ToggleButton onClick={toggleSidebar} collapsed={collapsed}>
            {collapsed ? <FaBars /> : <FaTimes />}
          </ToggleButton>
        </SidebarHeader>
        
        <NavItems>
          <NavItem to="/" active={isActive('/')} collapsed={collapsed}>
            <div className="icon"><FaHome /></div>
            <div className="text">Dashboard</div>
          </NavItem>
          
          <NavCategory collapsed={collapsed}>Products</NavCategory>
          
          <NavItem to="/product" active={isActive('/product')} collapsed={collapsed}>
            <div className="icon"><FaStore /></div>
            <div className="text">View Products</div>
          </NavItem>
          
          <NavItem to="/productadmin" active={isActive('/productadmin')} collapsed={collapsed}>
            <div className="icon"><FaClipboardList /></div>
            <div className="text">Manage Products</div>
          </NavItem>
          
          <NavCategory collapsed={collapsed}>Workshops</NavCategory>
          
          <NavItem to="/workshops" active={isActive('/workshops')} collapsed={collapsed}>
            <div className="icon"><FaUsers /></div>
            <div className="text">View Workshops</div>
          </NavItem>
          
          <NavItem to="/workshopadmin" active={isActive('/workshopadmin')} collapsed={collapsed}>
            <div className="icon"><FaChalkboardTeacher /></div>
            <div className="text">Manage Workshops</div>
          </NavItem>
          
          <NavCategory collapsed={collapsed}>Orders</NavCategory>
          
          <NavItem to="/orders" active={isActive('/orders')} collapsed={collapsed}>
            <div className="icon"><FaShoppingBag /></div>
            <div className="text">Confirmed Orders</div>
          </NavItem>
          
          <NavItem to="/order-management" active={isActive('/order-management')} collapsed={collapsed}>
            <div className="icon"><FaListAlt /></div>
            <div className="text">Order Management</div>
          </NavItem>
          
          <NavItem to="/payment-management" active={isActive('/payment-management')} collapsed={collapsed}>
            <div className="icon"><FaCreditCard /></div>
            <div className="text">Payment Management</div>
          </NavItem>
          
          <NavCategory collapsed={collapsed}>Analytics</NavCategory>
          
          <NavItem to="/analytics" active={isActive('/analytics')} collapsed={collapsed}>
            <div className="icon"><FaChartLine /></div>
            <div className="text">Reports & Analytics</div>
          </NavItem>
          
          <NavCategory collapsed={collapsed}>System</NavCategory>
          
          <NavItem to="/settings" active={isActive('/settings')} collapsed={collapsed}>
            <div className="icon"><FaCog /></div>
            <div className="text">System Settings</div>
          </NavItem>
          
          <NavItem as="button" onClick={handleSignOut} collapsed={collapsed}>
            <div className="icon"><FaSignOutAlt /></div>
            <div className="text">Sign Out</div>
          </NavItem>
        </NavItems>
      </Sidebar>
      
      <Content collapsed={collapsed}>
        <TopBar>
          <MobileMenuButton onClick={toggleMobileSidebar}>
            {mobileOpen ? <FaTimes /> : <FaBars />}
          </MobileMenuButton>
          
          <TopBarRight>
            <NotificationBell>
              <FaBell />
              <div className="badge">3</div>
            </NotificationBell>
            
            <UserProfile ref={userMenuRef} onClick={toggleUserMenu}>
              <div className="avatar">
                {user?.photoURL ? (
                  <img src={user.photoURL} alt="User" />
                ) : (
                  <FaUser />
                )}
              </div>
              <div className="user-info">
                <div className="name">{user?.displayName || 'Admin User'}</div>
                <div className="role">Administrator</div>
              </div>
              
              <UserDropdown isOpen={isUserMenuOpen}>
                <DropdownItem to="/profile">
                  <FaUser /> Profile
                </DropdownItem>
                <DropdownItem to="/settings">
                  <FaUserCog /> Settings
                </DropdownItem>
                <DropdownButton onClick={handleSignOut}>
                  <FaSignOutAlt /> Sign Out
                </DropdownButton>
              </UserDropdown>
            </UserProfile>
          </TopBarRight>
        </TopBar>
        
        <PageContent>
          <Outlet />
        </PageContent>
      </Content>
      
      <Backdrop show={mobileOpen} onClick={closeMobileSidebar} />
    </Container>
  );
};

export default Layout;